import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import { conteudosService } from '@/services/conteudosService'
import { documentoService } from '@/services/documentoService'
import { produtoService } from '@/services/produtoService'
import type { ConteudoPublicado, ConteudoResumo } from '@/types/conteudo'
import { PERFIS_VISIBILIDADE, TIPOS_CONTEUDO } from '@/types/conteudo'
import type { Produto } from '@/types/produto'
import { tokens } from '@/theme'
import PublicarMaterialDialog from './PublicarMaterialDialog'

/** Mesmo corte do backend (`CONSULTOR_OR_ABOVE`): onboarding puro cai em semPermissão, não em erro. */
const PAPEIS_CONHECIDOS = ['ADMIN', 'GERENTE', 'OPERADOR', 'CONSULTOR']

const formatarData = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR') : null

const vigenciaTexto = (c: ConteudoPublicado) => {
  const inicio = formatarData(c.vigenciaInicio)
  const fim = formatarData(c.vigenciaFim)
  return fim ? `${inicio} – ${fim}` : `desde ${inicio}`
}

/** KPIs de quem NÃO é administrador: contados da própria lista de vigentes que a tabela mostra. */
const resumoDaLista = (lista: ConteudoPublicado[]): ConteudoResumo => {
  const limite = new Date()
  limite.setDate(limite.getDate() + 30)
  const limiteIso = limite.toISOString().slice(0, 10)
  return {
    publicados: lista.length,
    vigentes: lista.length,
    produtosCobertos: new Set(lista.map((c) => c.produtoId).filter(Boolean)).size,
    vencendoEm30Dias: lista.filter((c) => c.vigenciaFim && c.vigenciaFim <= limiteIso).length,
  }
}

/**
 * Documentos e Materiais (Figma, Onda 7.2 / A12) — publicação tipada de guias comerciais,
 * checklists documentais e roteiros de reunião.
 *
 * ADMIN: vê tudo (ativos e inativos, `/v1/conteudos`), publica, desativa e sobe nova versão.
 * GERENTE/OPERADOR: veem só o que é vigente para o próprio perfil e tenant (`/v1/conteudos/vigentes`
 * — o corte é do backend, esta tela não filtra nada por conta própria).
 *
 * Consultor (inclusive CONSULTOR+OPERADOR, pelo critério do menu): nunca vê Guia comercial,
 * Checklist documental nem Roteiro de reunião (Q34 / "Instrução final"; Onda 5, Q-AN11-4) —
 * a rota some do menu dele e, por URL direta, a tela mostra "sem permissão" SEM chamar a API
 * (o servidor também devolve vazio para ele).
 *
 * O arquivo é baixado pelo endpoint auditado de documentos (`/arquivo`), nunca por URL do
 * storage. KPIs citam a mesma fonte da tabela: `/resumo` para o admin, a própria lista para os demais.
 */
export const DocumentosMateriaisPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const permissoes = useMemo(
    () => (usuario?.permissoes ?? []).map((p) => p.toUpperCase()),
    [usuario]
  )
  const isAdmin = permissoes.includes('ADMIN')
  const podeVer =
    permissoes.some((p) => PAPEIS_CONHECIDOS.includes(p)) && !ehConsultor(usuario?.permissoes)

  const [conteudos, setConteudos] = useState<ConteudoPublicado[] | null>(null)
  const [resumo, setResumo] = useState<ConteudoResumo | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [anterior, setAnterior] = useState<ConteudoPublicado | null>(null)
  const [selecionado, setSelecionado] = useState<ConteudoPublicado | null>(null)
  const [baixando, setBaixando] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    if (!podeVer) return
    setCarregando(true)
    setErro(null)
    try {
      if (isAdmin) {
        const [lista, kpis, catalogo] = await Promise.all([
          conteudosService.listar(),
          conteudosService.resumo(),
          produtoService.listar(0, 200),
        ])
        setConteudos(lista)
        setResumo(kpis)
        setProdutos(catalogo?.content ?? [])
      } else {
        const lista = await conteudosService.vigentes()
        setConteudos(lista)
        setResumo(resumoDaLista(lista))
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao listar materiais')
      setConteudos(null)
      setResumo(null)
    } finally {
      setCarregando(false)
    }
  }, [podeVer, isAdmin])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const baixar = async (c: ConteudoPublicado) => {
    setBaixando(c.id)
    setAviso(null)
    try {
      const blob = await documentoService.baixarArquivo(c.documentoId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${c.titulo} v${c.versao}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'Falha ao baixar o arquivo')
    } finally {
      setBaixando(null)
    }
  }

  const alterarStatus = async (c: ConteudoPublicado) => {
    setAviso(null)
    try {
      await conteudosService.alterarStatus(c.id, !c.ativo)
      setSelecionado(null)
      await carregar()
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'Não foi possível alterar o status')
    }
  }

  const colunas: ColunaTabelaAcao<ConteudoPublicado>[] = [
    {
      titulo: 'Título',
      render: (c) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {c.titulo}
          </Typography>
          {!c.ativo && (
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              Inativo
            </Typography>
          )}
        </Box>
      ),
    },
    { titulo: 'Tipo', render: (c) => TIPOS_CONTEUDO[c.tipo] ?? c.tipo },
    {
      titulo: 'Produto',
      render: (c) => c.produtoNome ?? (c.produtoId ? c.produtoId : 'Todos'),
    },
    { titulo: 'Versão', render: (c) => `v${c.versao}`, align: 'center', largura: 80 },
    {
      titulo: 'Vigência',
      render: (c) => (
        <Typography
          variant="body2"
          sx={{ color: c.vigente ? undefined : tokens.statusWarning }}
        >
          {vigenciaTexto(c)}
        </Typography>
      ),
    },
    {
      titulo: 'Visibilidade',
      render: (c) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {c.perfis.map((p) => (
            <Chip key={p} size="small" label={PERFIS_VISIBILIDADE[p] ?? p} />
          ))}
          {c.tenantId && (
            <Chip
              size="small"
              variant="outlined"
              label="Só este tenant"
              sx={{ borderColor: tokens.brandPrimary, color: tokens.brandPrimary }}
            />
          )}
        </Stack>
      ),
    },
  ]

  const cabecalho = (
    <PageHeader
      title="Documentos e Materiais"
      eyebrow="Administração"
      subtitle="Guias comerciais, checklists documentais e roteiros de reunião, por produto e perfil"
      acao={
        isAdmin ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAnterior(null)
              setDialogoAberto(true)
            }}
          >
            Publicar material
          </Button>
        ) : undefined
      }
    />
  )

  if (usuario && !podeVer) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista estado="semPermissao" />
      </Box>
    )
  }

  const lista = conteudos ?? []

  return (
    <Box>
      {cabecalho}

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}
      {aviso && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Publicados"
            valor={resumo ? resumo.publicados : '—'}
            legenda={isAdmin ? 'Peças ativas na base' : 'Peças que você pode ver'}
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Vigentes"
            valor={resumo ? resumo.vigentes : '—'}
            legenda="Dentro da vigência hoje"
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Produtos cobertos"
            valor={resumo ? resumo.produtosCobertos : '—'}
            legenda="Com pelo menos uma peça vigente"
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Vencendo em 30 dias"
            valor={resumo ? resumo.vencendoEm30Dias : '—'}
            legenda="Vigência termina no próximo mês"
            carregando={carregando}
          />
        </Grid>
      </Grid>

      {carregando && !conteudos ? (
        <EstadoDeLista estado="carregando" />
      ) : erro && !conteudos ? (
        <EstadoDeLista
          estado="erro"
          detalhe={erro}
          acao={{ label: 'Tentar de novo', onClick: () => void carregar() }}
        />
      ) : lista.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum material publicado"
          detalhe={
            isAdmin
              ? 'Comece pelo botão "Publicar material".'
              : 'Quando o administrador publicar um material para o seu perfil, ele aparece aqui.'
          }
        />
      ) : (
        <TabelaAcao<ConteudoPublicado>
          colunas={colunas}
          linhas={lista}
          onAbrir={(c) => (isAdmin ? setSelecionado(c) : void baixar(c))}
          renderAcao={(c) => (baixando === c.id ? 'Baixando…' : isAdmin ? 'Abrir →' : 'Baixar →')}
          chaveDaLinha={(c) => c.id}
        />
      )}

      {isAdmin && selecionado && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: `1px solid ${tokens.divider}`,
            borderRadius: 2,
            backgroundColor: tokens.surfaceCard,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {selecionado.titulo} · v{selecionado.versao}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
            {TIPOS_CONTEUDO[selecionado.tipo]} · {selecionado.produtoNome ?? 'Todos os produtos'} ·{' '}
            {vigenciaTexto(selecionado)} · {selecionado.ativo ? 'Ativo' : 'Inativo'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              onClick={() => void baixar(selecionado)}
              disabled={baixando === selecionado.id}
            >
              Baixar PDF
            </Button>
            {selecionado.ativo && (
              <Button
                variant="contained"
                onClick={() => {
                  setAnterior(selecionado)
                  setDialogoAberto(true)
                }}
              >
                Nova versão
              </Button>
            )}
            <Button
              color={selecionado.ativo ? 'warning' : 'success'}
              onClick={() => void alterarStatus(selecionado)}
            >
              {selecionado.ativo ? 'Desativar' : 'Reativar'}
            </Button>
            <Button onClick={() => setSelecionado(null)}>Fechar</Button>
          </Stack>
        </Box>
      )}

      {isAdmin && (
        <PublicarMaterialDialog
          aberto={dialogoAberto}
          anterior={anterior}
          produtos={produtos}
          onFechar={() => setDialogoAberto(false)}
          onPublicar={async (request) => {
            await conteudosService.publicar(request)
            await carregar()
          }}
          onNovaVersao={async (anteriorId, request) => {
            await conteudosService.novaVersao(anteriorId, request)
            setSelecionado(null)
            await carregar()
          }}
        />
      )}
    </Box>
  )
}

export default DocumentosMateriaisPage
